import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ChevronRight,
    ArrowLeft,
    FileText,
    PhoneOff,
    PhoneForwarded,
    Activity,
    Plus,
    Trash2,
    Edit,
    Edit3,
    X,
    Check,
    ChevronUp,
    ChevronDown,
    Loader2,
    Download,
    RefreshCw,
    Bot
} from 'lucide-react'
import { dialerApi, API_BASE_URL } from '../services/api'
import BotsTab from '../components/BotsTab'
import './DialerDetails.css'

// Default dispositions matching Angular initializeDispositions()
const BASE_DISPOSITIONS = [
    { name: 'Answering Machine', event_tag: 'answering-machine', selector: '#DispoSelectA > font:nth-child(1) > a', order_idx: 1 },
    { name: 'Busy', event_tag: 'busy', selector: '#DispoSelectA > font:nth-child(4) > a', order_idx: 2 },
    { name: 'Call Back', event_tag: 'call-back', selector: '#DispoSelectA > font:nth-child(7) > a', order_idx: 3 },
    { name: 'Dead Air', event_tag: 'dead-air', selector: '#DispoSelectA > font:nth-child(10) > a', order_idx: 4 },
    { name: 'Disconnected Number', event_tag: 'disconnected-number', selector: '#DispoSelectA > font:nth-child(13) > a', order_idx: 5 },
    { name: 'Declined Sale', event_tag: 'declined-sale', selector: '#DispoSelectB > font:nth-child(1) > a', order_idx: 6 },
    { name: 'Do Not Call', event_tag: 'do-not-call', selector: '#DispoSelectB > font:nth-child(4) > a', order_idx: 7 },
    { name: 'No Answer', event_tag: 'no-answer', selector: '#DispoSelectB > font:nth-child(7) > a', order_idx: 8 },
    { name: 'Not Interested', event_tag: 'not-interested', selector: '#DispoSelectB > font:nth-child(10) > a', order_idx: 9 },
    { name: 'No Pitch No Price', event_tag: 'no-pitch-no-price', selector: '#DispoSelectC > font:nth-child(1) > a', order_idx: 10 },
    { name: 'Sale Made', event_tag: 'sale-made', selector: '#DispoSelectC > font:nth-child(4) > a', order_idx: 11 },
    { name: 'Call Transfered', event_tag: 'call-transfered', selector: '#DispoSelectC > font:nth-child(7) > a', order_idx: 12 },
    { name: 'Abusive Customer', event_tag: 'abusive-customer', selector: '#DispoSelectB > font:nth-child(1) > a', order_idx: 13 },
    { name: 'Age Do Not Qualify', event_tag: 'age-do-not-qualify', selector: '#DispoSelectC > font:nth-child(10) > a', order_idx: 14 },
    { name: 'Business Line', event_tag: 'business-line', selector: '#DispoSelectB > font:nth-child(1) > a', order_idx: 15 },
    { name: 'Customer Busy', event_tag: 'customer-busy', selector: '#DispoSelectA > font:nth-child(4) > a', order_idx: 16 },
    { name: 'Too Many Calls', event_tag: 'too-many-calls', selector: '#DispoSelectB > font:nth-child(1) > a', order_idx: 17 }
]

function DialerDetails() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [dialer, setDialer] = useState(null)
    const [activeTab, setActiveTab] = useState('bots')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    // Tab data
    const [dispositions, setDispositions] = useState([])
    const [hangupSteps, setHangupSteps] = useState([])
    const [transferSteps, setTransferSteps] = useState([])
    const [selectedItems, setSelectedItems] = useState([])

    // Status tab - field on dialers table
    const [statusSelector, setStatusSelector] = useState('')
    const [statusChanged, setStatusChanged] = useState(false)

    // Modal state
    const [showModal, setShowModal] = useState(false)
    const [modalMode, setModalMode] = useState('add')
    const [currentItem, setCurrentItem] = useState(null)
    const [formData, setFormData] = useState({})

    // Initialize modal
    const [showInitModal, setShowInitModal] = useState(false)
    const [initType, setInitType] = useState('')
    const [initializing, setInitializing] = useState(false)

    // Export from another dialer
    const [showExportModal, setShowExportModal] = useState(false)
    const [allDialers, setAllDialers] = useState([])
    const [selectedDialerForExport, setSelectedDialerForExport] = useState('')

    // Bulk edit modal - inline editable form
    const [showBulkEditModal, setShowBulkEditModal] = useState(false)
    const [bulkEditItems, setBulkEditItems] = useState([])

    useEffect(() => {
        loadDialer()
        loadAllDialers()
    }, [id])

    useEffect(() => {
        if (dialer) {
            loadTabData()
            setSelectedItems([])
        }
    }, [dialer, activeTab])

    const loadDialer = async () => {
        setLoading(true)
        try {
            const res = await dialerApi.getById(id)
            const data = Array.isArray(res.data) ? res.data[0] : res.data
            if (data) {
                setDialer(data)
                setStatusSelector(data.status_selector || '#Tabs table tbody tr td:nth-child(5) img[src]')
            } else {
                setDialer(null)
            }
        } catch (error) {
            console.error('Failed to load dialer:', error)
            setDialer(null)
        } finally {
            setLoading(false)
        }
    }

    const loadAllDialers = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/tables/dialers`)
            const data = await res.json()
            setAllDialers(Array.isArray(data) ? data.filter(d => d.id != id) : [])
        } catch (error) {
            console.error('Failed to load dialers:', error)
        }
    }

    const loadTabData = async () => {
        try {
            if (activeTab === 'dispositions') {
                const res = await fetch(`${API_BASE_URL}/tables/dispositions?field=dialer_id&val=${id}`)
                const data = await res.json()
                // Sort by order_idx - matches Angular sort()
                const sorted = Array.isArray(data) ? data.sort((a, b) => (a.order_idx || 0) - (b.order_idx || 0)) : []
                setDispositions(sorted)
            } else if (activeTab === 'hangup') {
                const res = await fetch(`${API_BASE_URL}/tables/hangup_sequences?field=dialer_id&val=${id}`)
                const data = await res.json()
                const sorted = Array.isArray(data) ? data.sort((a, b) => (a.order_idx || 0) - (b.order_idx || 0)) : []
                setHangupSteps(sorted)
            } else if (activeTab === 'xfer') {
                const res = await fetch(`${API_BASE_URL}/tables/xfer_sequences?field=dialer_id&val=${id}`)
                const data = await res.json()
                const sorted = Array.isArray(data) ? data.sort((a, b) => (a.order_idx || 0) - (b.order_idx || 0)) : []
                setTransferSteps(sorted)
            } else if (activeTab === 'status') {
                setStatusSelector(dialer?.status_selector || '#Tabs table tbody tr td:nth-child(5) img[src]')
                setStatusChanged(false)
            }
            // 'bots' tab data is handled by BotsTab component itself
        } catch (error) {
            console.error('Failed to load tab data:', error)
        }
    }

    const tabs = [
        { id: 'dispositions', label: 'Dispositions', icon: FileText },
        { id: 'hangup', label: 'Hang Up Seq.', icon: PhoneOff },
        { id: 'xfer', label: 'Transfer Seq.', icon: PhoneForwarded },
        { id: 'status', label: 'Status', icon: Activity },
        { id: 'bots', label: 'Bots', icon: Bot }
    ]

    // Get table name for current tab
    const getTableName = () => {
        if (activeTab === 'dispositions') return 'dispositions'
        if (activeTab === 'hangup') return 'hangup_sequences'
        if (activeTab === 'xfer') return 'xfer_sequences'
        return ''
    }

    // Status handlers
    const handleStatusChange = (value) => {
        setStatusSelector(value)
        setStatusChanged(value !== (dialer?.status_selector || '#Tabs table tbody tr td:nth-child(5) img[src]'))
    }

    const saveStatus = async () => {
        setSaving(true)
        try {
            await fetch(`${API_BASE_URL}/tables/dialers/update/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: parseInt(id), status_selector: statusSelector })
            })
            loadDialer()
            setStatusChanged(false)
        } catch (error) {
            console.error('Failed to save status:', error)
        } finally {
            setSaving(false)
        }
    }

    // Modal handlers
    const openModal = (mode, item = null) => {
        setModalMode(mode)
        setCurrentItem(item)

        if (activeTab === 'dispositions') {
            setFormData(item ? {
                name: item.name,
                event_tag: item.event_tag,
                selector: item.selector,
                order_idx: item.order_idx
            } : { name: '', event_tag: '', selector: '', order_idx: dispositions.length + 1 })
        } else {
            // Hangup or Transfer sequence
            const currentSteps = activeTab === 'hangup' ? hangupSteps : transferSteps
            setFormData(item ? {
                description: item.description,
                action: item.action,
                selector: item.selector,
                order_idx: item.order_idx,
                timeout_before: item.timeout_before,
                timeout_after: item.timeout_after,
                set_consultative_transfer: item.set_consultative_transfer || false
            } : {
                description: '',
                action: 'click',
                selector: '',
                order_idx: currentSteps.length + 1,
                timeout_before: 1000,
                timeout_after: 0,
                set_consultative_transfer: false
            })
        }
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)

        try {
            const tableName = getTableName()
            let dataToSave = { ...formData, dialer_id: parseInt(id) }

            if (activeTab === 'dispositions') {
                dataToSave.order_idx = parseInt(formData.order_idx) || dispositions.length + 1
            } else {
                dataToSave.order_idx = parseInt(formData.order_idx) || 1
                dataToSave.timeout_before = parseInt(formData.timeout_before) || 0
                dataToSave.timeout_after = parseInt(formData.timeout_after) || 0
            }

            if (modalMode === 'add') {
                await fetch(`${API_BASE_URL}/tables/${tableName}/add`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(dataToSave)
                })
            } else {
                await fetch(`${API_BASE_URL}/tables/${tableName}/update/${currentItem.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...dataToSave, id: currentItem.id })
                })
            }

            loadTabData()
            setShowModal(false)
        } catch (error) {
            console.error('Failed to save:', error)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (item) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return

        try {
            const tableName = getTableName()
            await fetch(`${API_BASE_URL}/tables/${tableName}/delete?field=id&val=${item.id}`, {
                method: 'DELETE'
            })
            loadTabData()
        } catch (error) {
            console.error('Failed to delete:', error)
        }
    }

    // Bulk delete
    const handleDeleteSelected = async () => {
        if (selectedItems.length === 0) return
        if (!window.confirm(`Delete ${selectedItems.length} selected items?`)) return

        setSaving(true)
        const tableName = getTableName()

        for (const item of selectedItems) {
            try {
                await fetch(`${API_BASE_URL}/tables/${tableName}/delete?field=id&val=${item.id}`, {
                    method: 'DELETE'
                })
            } catch (error) {
                console.error('Failed to delete item:', error)
            }
        }

        setSelectedItems([])
        setSaving(false)
        loadTabData()
    }

    // Checkbox selection
    const toggleSelection = (item) => {
        setSelectedItems(prev => {
            const exists = prev.find(i => i.id === item.id)
            if (exists) {
                return prev.filter(i => i.id !== item.id)
            } else {
                return [...prev, item]
            }
        })
    }

    const isSelected = (item) => selectedItems.some(i => i.id === item.id)

    // Move up/down for dispositions and sequences - matches Angular moveUp/moveDown
    const moveItem = async (index, direction) => {
        let items, tableName

        if (activeTab === 'dispositions') {
            items = [...dispositions]
            tableName = 'dispositions'
        } else if (activeTab === 'hangup') {
            items = [...hangupSteps]
            tableName = 'hangup_sequences'
        } else {
            items = [...transferSteps]
            tableName = 'xfer_sequences'
        }

        if (direction === 'up' && index === 0) return
        if (direction === 'down' && index === items.length - 1) return

        const swapIndex = direction === 'up' ? index - 1 : index + 1

        // Swap order_idx values - matches Angular
        const tempIdx = items[index].order_idx
        items[index].order_idx = items[swapIndex].order_idx
        items[swapIndex].order_idx = tempIdx

        // Update both records
        try {
            await fetch(`${API_BASE_URL}/tables/${tableName}/update/${items[index].id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(items[index])
            })
            await fetch(`${API_BASE_URL}/tables/${tableName}/update/${items[swapIndex].id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(items[swapIndex])
            })
            loadTabData()
        } catch (error) {
            console.error('Failed to reorder:', error)
        }
    }

    // Initialize dispositions - matches Angular initializeDispositions()
    const initializeDispositions = async () => {
        setInitializing(true)

        // Process each disposition sequentially - matches Angular processItem()
        for (const dispo of BASE_DISPOSITIONS) {
            try {
                await fetch(`${API_BASE_URL}/tables/dispositions/add`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...dispo,
                        dialer_id: parseInt(id)
                    })
                })
            } catch (error) {
                console.error('Failed to add disposition:', error)
            }
        }

        setInitializing(false)
        loadTabData()
    }

    // Export dispositions from another dialer
    const exportFromDialer = async () => {
        if (!selectedDialerForExport) return

        setInitializing(true)
        try {
            // Load dispositions from selected dialer
            const res = await fetch(`${API_BASE_URL}/tables/dispositions?field=dialer_id&val=${selectedDialerForExport}`)
            const sourceDispositions = await res.json()

            if (Array.isArray(sourceDispositions) && sourceDispositions.length > 0) {
                // Copy each disposition to current dialer
                for (const dispo of sourceDispositions) {
                    await fetch(`${API_BASE_URL}/tables/dispositions/add`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            name: dispo.name,
                            event_tag: dispo.event_tag,
                            selector: dispo.selector,
                            order_idx: dispo.order_idx,
                            dialer_id: parseInt(id)
                        })
                    })
                }
            }

            setShowExportModal(false)
            setSelectedDialerForExport('')
            loadTabData()
        } catch (error) {
            console.error('Failed to export dispositions:', error)
        } finally {
            setInitializing(false)
        }
    }

    // Bulk edit functions - inline editable form
    const openBulkEditModal = () => {
        // Create editable copies of selected items
        const editableItems = selectedItems.map(item => ({
            ...item,
            _original: { ...item } // Keep original for comparison
        }))
        setBulkEditItems(editableItems)
        setShowBulkEditModal(true)
    }

    const updateBulkEditItem = (index, field, value) => {
        setBulkEditItems(prev => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
    }

    const hasItemChanged = (item) => {
        return item.name !== item._original.name ||
               item.event_tag !== item._original.event_tag ||
               item.selector !== item._original.selector
    }

    const saveBulkEdit = async () => {
        const itemsToUpdate = bulkEditItems.filter(hasItemChanged)
        if (itemsToUpdate.length === 0) {
            setShowBulkEditModal(false)
            return
        }

        setSaving(true)
        try {
            for (const item of itemsToUpdate) {
                const updateData = {
                    id: item.id,
                    name: item.name,
                    event_tag: item.event_tag,
                    selector: item.selector,
                    order_idx: item.order_idx,
                    dialer_id: parseInt(id)
                }

                await fetch(`${API_BASE_URL}/tables/dispositions/update/${item.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData)
                })
            }

            setShowBulkEditModal(false)
            setSelectedItems([])
            loadTabData()
        } catch (error) {
            console.error('Failed to save bulk edit:', error)
        } finally {
            setSaving(false)
        }
    }

    // Initialize sequences with default steps
    const initializeSteps = async (type) => {
        setInitializing(true)
        const tableName = getTableName()

        let baseSteps = []

        if (activeTab === 'hangup') {
            baseSteps = [
                { dialer_id: parseInt(id), order_idx: 1, description: 'Click on transfer control', action: 'click', selector: '#XferControl > a > img', timeout_before: 1500, timeout_after: 0 },
                { dialer_id: parseInt(id), order_idx: 2, description: 'Click on...', action: 'click', selector: '#TransferMaindiv > font > table > tbody > tr:nth-child(4) > td > font > a:nth-child(1)', timeout_before: 1500, timeout_after: 0 },
                { dialer_id: parseInt(id), order_idx: 3, description: 'Click on blind transfer', action: 'click', selector: '#DialBlindTransfer > a', timeout_before: 1500, timeout_after: 1000 }
            ]
        } else if (activeTab === 'xfer') {
            if (type === 'd1_transfer') {
                baseSteps = [
                    { dialer_id: parseInt(id), order_idx: 1, description: 'Click on transfer control', action: 'click', selector: '#XferControl > a > img', timeout_before: 1000, timeout_after: 0, set_consultative_transfer: false },
                    { dialer_id: parseInt(id), order_idx: 2, description: 'Click on...', action: 'click', selector: '#TransferMaindiv > font > table > tbody > tr:nth-child(4) > td > font > a:nth-child(1)', timeout_before: 1500, timeout_after: 0, set_consultative_transfer: false },
                    { dialer_id: parseInt(id), order_idx: 3, description: 'Click on blind transfer', action: 'click', selector: '#DialBlindTransfer > a', timeout_before: 1500, timeout_after: 0, set_consultative_transfer: false }
                ]
            } else if (type === 'local_closer') {
                baseSteps = [
                    { dialer_id: parseInt(id), order_idx: 1, description: 'Click on transfer control', action: 'click', selector: '#XferControl > a > img', timeout_before: 1000, timeout_after: 0, set_consultative_transfer: false },
                    { dialer_id: parseInt(id), order_idx: 2, description: 'Set consultative transfer', action: 'click', selector: '#consultativexfer', timeout_before: 1000, timeout_after: 0, set_consultative_transfer: true },
                    { dialer_id: parseInt(id), order_idx: 3, description: 'Click on local closer', action: 'click', selector: '#LocalCloser > a > img', timeout_before: 1500, timeout_after: 1000, set_consultative_transfer: false }
                ]
            } else if (type === 'dial_with_customer') {
                baseSteps = [
                    { dialer_id: parseInt(id), order_idx: 1, description: 'Click on transfer control', action: 'click', selector: '#XferControl > a > img', timeout_before: 1000, timeout_after: 0, set_consultative_transfer: false },
                    { dialer_id: parseInt(id), order_idx: 2, description: 'Set consultative transfer', action: 'click', selector: '#consultativexfer', timeout_before: 1500, timeout_after: 0, set_consultative_transfer: true },
                    { dialer_id: parseInt(id), order_idx: 3, description: 'Click dial with customer', action: 'click', selector: '#DialWithCustomer > a > img', timeout_before: 2000, timeout_after: 0, set_consultative_transfer: false },
                    { dialer_id: parseInt(id), order_idx: 4, description: 'Click leave 3 way call', action: 'click', selector: '#Leave3WayCall > a > img', timeout_before: 2000, timeout_after: 0, set_consultative_transfer: false }
                ]
            }
        }

        // Insert all steps
        for (const step of baseSteps) {
            try {
                await fetch(`${API_BASE_URL}/tables/${tableName}/add`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(step)
                })
            } catch (error) {
                console.error('Failed to add step:', error)
            }
        }

        setInitializing(false)
        setShowInitModal(false)
        loadTabData()
    }

    if (loading) {
        return (
            <div className="loading-state">
                <div className="loading-spinner" />
                <p>Loading dialer...</p>
            </div>
        )
    }

    if (!dialer) {
        return (
            <div className="empty-state">
                <h3>Dialer not found</h3>
                <button className="btn btn-primary" onClick={() => navigate('/dialers')}>
                    Back to Dialers
                </button>
            </div>
        )
    }

    return (
        <motion.div className="avatar-details" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">Dashboard</Link>
                <ChevronRight size={16} />
                <Link to="/dialers">Dialers</Link>
                <ChevronRight size={16} />
                <span>{dialer?.name}</span>
            </div>

            {/* Header */}
            <div className="detail-header">
                <Link to="/dialers" className="back-link">
                    <ArrowLeft size={20} />
                </Link>
                <div className="detail-avatar" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
                    {dialer?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="detail-info">
                    <h1 className="detail-title">{dialer?.name}</h1>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                {tabs.map(tab => (
                    <motion.button
                        key={tab.id}
                        className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        whileHover={{ y: -2 }}
                        whileTap={{ y: 0 }}
                    >
                        <tab.icon size={18} />
                        {tab.label}
                        {activeTab === tab.id && (
                            <motion.div className="tab-indicator" layoutId="dialer-tab-indicator" transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                        )}
                    </motion.button>
                ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
                <motion.div key={activeTab} className="tab-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>

                    {/* DISPOSITIONS TAB */}
                    {activeTab === 'dispositions' && (
                        <div className="licenses-tab">
                            <div className="tab-header">
                                <h2>Dispositions</h2>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {selectedItems.length > 0 && (
                                        <>
                                            <button className="btn btn-secondary btn-sm" onClick={openBulkEditModal} disabled={saving}>
                                                <Edit3 size={16} /> Bulk Edit ({selectedItems.length})
                                            </button>
                                            <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={saving}>
                                                <Trash2 size={16} /> Delete ({selectedItems.length})
                                            </button>
                                        </>
                                    )}
                                    <button className="btn btn-primary btn-sm" onClick={() => openModal('add')}>
                                        <Plus size={16} /> Add Disposition
                                    </button>
                                </div>
                            </div>

                            {dispositions.length === 0 ? (
                                <div className="empty-state">
                                    <FileText size={48} />
                                    <p style={{ marginBottom: '8px' }}><em>The dispositions for this dialer have not been initialized.</em></p>
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={initializeDispositions}
                                            disabled={initializing}
                                        >
                                            {initializing ? (
                                                <><Loader2 size={16} className="spin" /> Initializing...</>
                                            ) : (
                                                <><RefreshCw size={16} /> Initialize dispositions</>
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setShowExportModal(true)}
                                            disabled={initializing}
                                        >
                                            <Download size={16} /> Export from another dialer
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}></th>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Name</th>
                                                <th>Event Tag</th>
                                                <th>Selector</th>
                                                <th style={{ width: '80px' }}></th>
                                                <th style={{ width: '80px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dispositions.map((item, index) => (
                                                <tr key={item.id} className={isSelected(item) ? 'selected' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(item)}
                                                            onChange={() => toggleSelection(item)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td>{item.order_idx || index + 1}</td>
                                                    <td>{item.name}</td>
                                                    <td><code style={{ fontSize: '0.8rem', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '4px', color: 'var(--primary)' }}>{item.event_tag}</code></td>
                                                    <td><code style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{item.selector}</code></td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '2px' }}>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'up')} disabled={index === 0}><ChevronUp size={14} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'down')} disabled={index === dispositions.length - 1}><ChevronDown size={14} /></button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '2px' }}>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => openModal('edit', item)}><Edit size={14} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(item)}><Trash2 size={14} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* HANGUP SEQUENCE TAB */}
                    {activeTab === 'hangup' && (
                        <div className="licenses-tab">
                            <div className="tab-header">
                                <h2>Hangup Sequence</h2>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {selectedItems.length > 0 && (
                                        <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={saving}>
                                            <Trash2 size={16} /> Delete Selected ({selectedItems.length})
                                        </button>
                                    )}
                                    <button className="btn btn-primary btn-sm" onClick={() => openModal('add')}>
                                        <Plus size={16} /> Add Step
                                    </button>
                                </div>
                            </div>

                            {hangupSteps.length === 0 ? (
                                <div className="empty-state">
                                    <PhoneOff size={48} />
                                    <p style={{ marginBottom: '16px' }}>The hangup steps for this dialer have not been initialized.</p>
                                    <button className="btn btn-primary" onClick={() => initializeSteps('hangup')}>
                                        {initializing ? <><Loader2 size={16} className="spin" /> Initializing...</> : 'Initialize Hangup Steps'}
                                    </button>
                                </div>
                            ) : (
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}></th>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Description</th>
                                                <th style={{ width: '80px' }}>Action</th>
                                                <th>Selector</th>
                                                <th style={{ width: '80px' }}>Before</th>
                                                <th style={{ width: '80px' }}>After</th>
                                                <th style={{ width: '100px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hangupSteps.map((item, index) => (
                                                <tr key={item.id} className={isSelected(item) ? 'selected' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(item)}
                                                            onChange={() => toggleSelection(item)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td>{item.order_idx}</td>
                                                    <td>{item.description}</td>
                                                    <td><code style={{ fontSize: '0.75rem' }}>{item.action}</code></td>
                                                    <td><code style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{item.selector}</code></td>
                                                    <td>{item.timeout_before}ms</td>
                                                    <td>{item.timeout_after}ms</td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '2px' }}>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'up')} disabled={index === 0}><ChevronUp size={16} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'down')} disabled={index === hangupSteps.length - 1}><ChevronDown size={16} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => openModal('edit', item)}><Edit size={16} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TRANSFER SEQUENCE TAB */}
                    {activeTab === 'xfer' && (
                        <div className="licenses-tab">
                            <div className="tab-header">
                                <h2>Transfer Sequence</h2>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {selectedItems.length > 0 && (
                                        <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={saving}>
                                            <Trash2 size={16} /> Delete Selected ({selectedItems.length})
                                        </button>
                                    )}
                                    <button className="btn btn-primary btn-sm" onClick={() => openModal('add')}>
                                        <Plus size={16} /> Add Step
                                    </button>
                                </div>
                            </div>

                            {transferSteps.length === 0 ? (
                                <div className="empty-state">
                                    <PhoneForwarded size={48} />
                                    <p style={{ marginBottom: '16px' }}>The transfer steps for this dialer have not been initialized.</p>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <button className="btn btn-primary" onClick={() => { setInitType('d1_transfer'); setShowInitModal(true) }}>
                                            D1 Transfer
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => { setInitType('local_closer'); setShowInitModal(true) }}>
                                            Local Closer
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => { setInitType('dial_with_customer'); setShowInitModal(true) }}>
                                            Dial With Customer
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}></th>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Description</th>
                                                <th style={{ width: '80px' }}>Action</th>
                                                <th>Selector</th>
                                                <th style={{ width: '80px' }}>Before</th>
                                                <th style={{ width: '80px' }}>After</th>
                                                <th style={{ width: '60px' }}>Consult</th>
                                                <th style={{ width: '100px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transferSteps.map((item, index) => (
                                                <tr key={item.id} className={isSelected(item) ? 'selected' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(item)}
                                                            onChange={() => toggleSelection(item)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td>{item.order_idx}</td>
                                                    <td>{item.description}</td>
                                                    <td><code style={{ fontSize: '0.75rem' }}>{item.action}</code></td>
                                                    <td><code style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{item.selector}</code></td>
                                                    <td>{item.timeout_before}ms</td>
                                                    <td>{item.timeout_after}ms</td>
                                                    <td>{item.set_consultative_transfer ? '✓' : ''}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '2px' }}>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'up')} disabled={index === 0}><ChevronUp size={16} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => moveItem(index, 'down')} disabled={index === transferSteps.length - 1}><ChevronDown size={16} /></button>
                                                            <button className="btn btn-ghost btn-sm" onClick={() => openModal('edit', item)}><Edit size={16} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STATUS TAB */}
                    {activeTab === 'status' && (
                        <div className="licenses-tab">
                            <div className="tab-header">
                                <h2>Status</h2>
                            </div>

                            <div style={{ maxWidth: '600px', padding: '20px 0' }}>
                                <div className="input-group">
                                    <label className="input-label">Status Selector</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={statusSelector}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        placeholder="CSS selector for status element"
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '8px' }}>
                                        CSS selector used to detect call status in Vicidial interface
                                    </p>
                                </div>

                                {statusChanged && (
                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '24px', textAlign: 'center' }}>
                                        <button className="btn btn-primary" onClick={saveStatus} disabled={saving}>
                                            {saving ? <><Loader2 size={18} className="spin" /> Saving...</> : <><Check size={18} /> Save</>}
                                        </button>
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* BOTS TAB (v3.92) - Modern Design */}
                    {activeTab === 'bots' && (
                        <BotsTab dialerId={id} />
                    )}
                </motion.div>
            </AnimatePresence>

            {/* ADD/EDIT MODAL */}
            <AnimatePresence>
                {showModal && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)}>
                        <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    {modalMode === 'add' ? 'Add' : 'Edit'} {activeTab === 'dispositions' ? 'Disposition' : 'Step'}
                                </h2>
                                <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    {activeTab === 'dispositions' ? (
                                        <>
                                            <div className="input-group">
                                                <label className="input-label">Name</label>
                                                <input type="text" className="input" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Answering Machine" required />
                                            </div>
                                            <div className="input-group">
                                                <label className="input-label">Event Tag</label>
                                                <input type="text" className="input" value={formData.event_tag || ''} onChange={(e) => setFormData({ ...formData, event_tag: e.target.value })} placeholder="e.g. answering-machine" required />
                                            </div>
                                            <div className="input-group">
                                                <label className="input-label">Selector</label>
                                                <input type="text" className="input" value={formData.selector || ''} onChange={(e) => setFormData({ ...formData, selector: e.target.value })} placeholder="e.g. #DispoSelectA > font:nth-child(1) > a" required />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="input-group">
                                                <label className="input-label">Description</label>
                                                <input type="text" className="input" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Click on transfer control" />
                                            </div>
                                            <div className="input-group">
                                                <label className="input-label">Action</label>
                                                <select className="input" value={formData.action || 'click'} onChange={(e) => setFormData({ ...formData, action: e.target.value })}>
                                                    <option value="click">click</option>
                                                    <option value="wait">wait</option>
                                                    <option value="type">type</option>
                                                    <option value="press">press</option>
                                                </select>
                                            </div>
                                            <div className="input-group">
                                                <label className="input-label">Selector</label>
                                                <input type="text" className="input" value={formData.selector || ''} onChange={(e) => setFormData({ ...formData, selector: e.target.value })} placeholder="CSS selector" />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                <div className="input-group">
                                                    <label className="input-label">Timeout Before (ms)</label>
                                                    <input type="number" className="input" value={formData.timeout_before || 0} onChange={(e) => setFormData({ ...formData, timeout_before: e.target.value })} />
                                                </div>
                                                <div className="input-group">
                                                    <label className="input-label">Timeout After (ms)</label>
                                                    <input type="number" className="input" value={formData.timeout_after || 0} onChange={(e) => setFormData({ ...formData, timeout_after: e.target.value })} />
                                                </div>
                                            </div>
                                            {activeTab === 'xfer' && (
                                                <div className="input-group" style={{ marginTop: '8px' }}>
                                                    <label className="checkbox-wrapper">
                                                        <input type="checkbox" className="checkbox" checked={formData.set_consultative_transfer || false} onChange={(e) => setFormData({ ...formData, set_consultative_transfer: e.target.checked })} />
                                                        <span>Set Consultative Transfer</span>
                                                    </label>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving ? <><Loader2 size={18} className="spin" /> Saving...</> : <><Check size={18} /> {modalMode === 'add' ? 'Create' : 'Save'}</>}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* INIT SEQUENCE MODAL */}
            <AnimatePresence>
                {showInitModal && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !initializing && setShowInitModal(false)}>
                        <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">Initialize Transfer Steps</h2>
                                <button className="modal-close" onClick={() => !initializing && setShowInitModal(false)}><X size={20} /></button>
                            </div>

                            <div className="modal-body">
                                <p>This will initialize the transfer sequence with the <strong>{initType.replace(/_/g, ' ')}</strong> template.</p>
                                <p style={{ color: 'var(--text-muted)', marginTop: '12px' }}>You can modify the steps after initialization.</p>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowInitModal(false)} disabled={initializing}>Cancel</button>
                                <button className="btn btn-primary" onClick={() => initializeSteps(initType)} disabled={initializing}>
                                    {initializing ? <><Loader2 size={18} className="spin" /> Initializing...</> : <><Check size={18} /> Initialize</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* EXPORT FROM DIALER MODAL */}
            <AnimatePresence>
                {showExportModal && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !initializing && setShowExportModal(false)}>
                        <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">Export from Another Dialer</h2>
                                <button className="modal-close" onClick={() => !initializing && setShowExportModal(false)}><X size={20} /></button>
                            </div>

                            <div className="modal-body">
                                <div className="input-group">
                                    <label className="input-label">Select Source Dialer</label>
                                    <select
                                        className="input"
                                        value={selectedDialerForExport}
                                        onChange={(e) => setSelectedDialerForExport(e.target.value)}
                                    >
                                        <option value="">Select a dialer...</option>
                                        {allDialers.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <p style={{ color: 'var(--text-muted)', marginTop: '12px', fontSize: '0.85rem' }}>
                                    This will copy all dispositions from the selected dialer to this one.
                                </p>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowExportModal(false)} disabled={initializing}>Cancel</button>
                                <button className="btn btn-primary" onClick={exportFromDialer} disabled={initializing || !selectedDialerForExport}>
                                    {initializing ? <><Loader2 size={18} className="spin" /> Exporting...</> : <><Download size={18} /> Export</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* BULK EDIT MODAL - Inline Editable Form */}
            <AnimatePresence>
                {showBulkEditModal && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !saving && setShowBulkEditModal(false)}>
                        <motion.div className="modal modal-xl dispositions-bulk-modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    <Edit3 size={20} /> Bulk Edit {bulkEditItems.length} Dispositions
                                </h2>
                                <button className="modal-close" onClick={() => !saving && setShowBulkEditModal(false)}><X size={20} /></button>
                            </div>

                            <div className="modal-body" style={{ padding: 0 }}>
                                <p style={{ color: 'var(--text-muted)', padding: '16px 20px 0', fontSize: '0.875rem', margin: 0 }}>
                                    Edit the fields below. Changes are highlighted. Click "Save All" to apply.
                                </p>

                                <div className="bulk-edit-table-wrapper">
                                    <table className="bulk-edit-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}>#</th>
                                                <th style={{ width: '180px' }}>Name</th>
                                                <th style={{ width: '160px' }}>Event Tag</th>
                                                <th>Selector</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkEditItems.map((item, index) => {
                                                const changed = hasItemChanged(item)
                                                // Check if selector matches the standard pattern (allow optional letter for editing flow)
                                                const selectorPattern = /#DispoSelect([A-Za-z]?) > font:nth-child\((\d+)\) > a/
                                                const match = item.selector?.match(selectorPattern)

                                                return (
                                                    <tr key={item.id} className={changed ? 'row-changed' : ''}>
                                                        <td className="order-cell">{item.order_idx}</td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="bulk-input readonly"
                                                                value={item.name}
                                                                readOnly
                                                                title="Name is not editable in bulk"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="bulk-input readonly"
                                                                value={item.event_tag}
                                                                readOnly
                                                                title="Event Tag is not editable in bulk"
                                                            />
                                                        </td>
                                                        <td>
                                                            {match ? (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <div className="selector-edit-group">
                                                                        <span className="selector-label">Col</span>
                                                                        <input
                                                                            type="text"
                                                                            className={`bulk-input short ${item.selector !== item._original.selector ? 'field-changed' : ''}`}
                                                                            value={match[1]}
                                                                            maxLength={1}
                                                                            onChange={(e) => {
                                                                                const newLetter = e.target.value.toUpperCase()
                                                                                // Allow empty string to let user backspace, but otherwise enforce A-Z
                                                                                if (newLetter === '' || /^[A-Z]$/.test(newLetter)) {
                                                                                    const newSelector = `#DispoSelect${newLetter} > font:nth-child(${match[2]}) > a`
                                                                                    updateBulkEditItem(index, 'selector', newSelector)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="selector-edit-group">
                                                                        <span className="selector-label">Row</span>
                                                                        <input
                                                                            type="number"
                                                                            className={`bulk-input short ${item.selector !== item._original.selector ? 'field-changed' : ''}`}
                                                                            value={match[2]}
                                                                            onChange={(e) => {
                                                                                const newNum = e.target.value
                                                                                const newSelector = `#DispoSelect${match[1]} > font:nth-child(${newNum}) > a`
                                                                                updateBulkEditItem(index, 'selector', newSelector)
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="selector-preview">
                                                                        {item.selector}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    className={`bulk-input ${item.selector !== item._original.selector ? 'field-changed' : ''}`}
                                                                    value={item.selector}
                                                                    onChange={(e) => updateBulkEditItem(index, 'selector', e.target.value)}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                        {bulkEditItems.filter(hasItemChanged).length} of {bulkEditItems.length} items modified
                                    </span>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkEditModal(false)} disabled={saving}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={saveBulkEdit}
                                    disabled={saving}
                                >
                                    {saving ? <><Loader2 size={18} className="spin" /> Saving...</> : <><Check size={18} /> Save All</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default DialerDetails